/**
 * The panel for fixed editors below the dashboard's tiles (Shawn 2026-11-03, v2 redesign
 * spec #325, approved Lavish review `.lavish/fixed-week-mode.html`, server side
 * `fixed-week-panel.ts`).
 *
 * One column per brand, black with a thin line, holding compact grey cards: a status pill,
 * a title that opens the card, price above its estimated length (both right-aligned, level
 * with the title) and up to two 36px buttons. A card carries no deadline timer of its own -
 * only the top strip (every card's shared weekly deadline) and an open revision's own 48h
 * clock count down; an offered or unrated card gets a countdown only in its last 24h before
 * it goes to priority dispatch. A card running on a date of its own (it was running when its
 * brand switched on) states that date, no timer. Up to three columns fit, more scroll
 * sideways; on a phone wrapping tabs (with a card count) switch between brands one at a time,
 * no sideways scroll. Brands with every card handed in sort last and sit dimmed. A brand with
 * nothing for this editor to see this week has no column and no tab at all (Shawn 2026-11-04);
 * when that is every brand, the panel says so in one line. Another editor's share and next
 * week's cards are not shown, except a card a client assigned to this editor for next week,
 * because they were told about it.
 *
 * Two sheets: Release card (a confirm window, on a client-assigned card with an optional note
 * for the client) and Hand in (Drive brands get the link field). These cards live here and only here: the Card tile
 * skips them (`CardPanel`), and they offer Release instead of the penalised Give it back.
 *
 * @module web/screens/FixedWeekPanel
 */
import { useEffect, useMemo, useState } from "react";
import { ApiError, post } from "../api/client.ts";
import type { EditorCard, FixedWeekBrandBlock, FixedWeekPanelData, FixedWeekRow, PushTerms } from "../api/types.ts";
import { useI18n, type I18n, type MessageKey } from "../i18n/index.tsx";
import { DriveHandin } from "./DriveHandin.tsx";
import { editorCardHref, graceMinutesLeft, initials } from "./editorFormat.ts";

type OnActed = (note?: string) => void;

const REVISION_HOURS = 48;
const SOON_MS = 24 * 3_600_000;
const HANDIN_URL = "https://feedback.aditor.ai/handin";

type Sheet = { kind: "push" | "handin"; id: string };

const ACCEPT_ERRORS: Record<string, MessageKey> = {
  share_other: "fixedWeek.errShare",
  next_week: "fixedWeek.errNextWeek",
  revision_open: "fixedWeek.errRevision",
  paused: "fixedWeek.errPaused",
  locked: "fixedWeek.errPaused",
  waiting_rate: "fixedWeek.errRate",
};

/** Times on the panel read in the editor's saved zone; an unknown zone falls back to the browser's. */
function inZone(iso: string, i18n: I18n, zone: string | null, opts: Intl.DateTimeFormatOptions): string {
  try {
    return i18n.dateTime(iso, zone ? { ...opts, timeZone: zone } : opts);
  } catch {
    return i18n.dateTime(iso, opts);
  }
}

/** "Sun 8 Nov, 23:59". */
function day(iso: string, i18n: I18n, zone: string | null): string {
  return inZone(iso, i18n, zone, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).replace(/^([A-Za-z]+),/, "$1");
}

/** "Wednesday 23:59": the free window of a Release. */
function longDay(iso: string, i18n: I18n, zone: string | null): string {
  return inZone(iso, i18n, zone, { weekday: "long", hour: "2-digit", minute: "2-digit" }).replace(",", "");
}

/** "6d 13h left", and with seconds under 24h. */
function timeLeft(ms: number, t: I18n["t"]): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  return d ? t("fixedWeek.leftDays", { d, h }) : t("fixedWeek.leftHours", { h, m, s: total % 60 });
}

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** The Release confirm window, shared with the card page. It says what it costs, from the server, and posts to the release route only. On a client-assigned card an optional note for the client earns the editor trust, once per card, and is posted on the Trello card. */
export function ReleaseCardConfirm({
  workItemId,
  title,
  terms,
  onDone,
  onCancel,
  primaryClass,
  buttonClass,
  timezone = null,
}: {
  workItemId: string;
  title: string;
  terms: PushTerms;
  onDone: (note?: string) => void;
  onCancel: () => void;
  primaryClass: string;
  buttonClass: string;
  timezone?: string | null;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [note, setNote] = useState("");
  async function push() {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      const trimmed = terms.noteForClient ? note.trim() : "";
      const result = await post<{ ok: boolean; message?: string }>(`/api/editor/work/${workItemId}/push`, trimmed ? { note: trimmed } : undefined);
      onDone(result?.message);
    } catch {
      setError(true);
      setBusy(false);
    }
  }
  const when = terms.freeUntil ? longDay(terms.freeUntil, i18n, timezone) : "";
  const late = terms.lateTrustCost;
  const cost = !terms.free
    ? terms.lateWindow
      ? t("fixedWeek.releaseLastDay", { hours: terms.cooldownHours, trust: late })
      : terms.trustCost > 0
        ? t("fixedWeek.releaseLateTrust", { when, hours: terms.cooldownHours, trust: terms.trustCost, late })
        : t("fixedWeek.releaseLate", { when, hours: terms.cooldownHours, late })
    : terms.freeUntil
      ? t("fixedWeek.releaseFreeUntil", { when, late })
      : t("fixedWeek.releaseFree");
  return (
    <div className="fw-modal">
      <div className="fw-sheet" data-fw-sheet role="alertdialog" aria-modal="true" aria-label={t("fixedWeek.releaseCard")}>
        <p className="fw-sheet-title">{t("fixedWeek.releaseConfirm")}</p>
        <p className="fw-sheet-body">{t(terms.fixedOnly ? "fixedWeek.releaseBodyFixedOnly" : "fixedWeek.releaseBody", { title })}</p>
        <p className="fw-sheet-hint">{cost}</p>
        {terms.noteForClient && (
          <textarea
            className="fw-sheet-note"
            data-fw-release-note
            aria-label={t("fixedWeek.releaseNotePlaceholder")}
            placeholder={t("fixedWeek.releaseNotePlaceholder")}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
          />
        )}
        {error && <p className="fw-error" role="alert">{t("fixedWeek.errRelease")}</p>}
        <div className="fw-sheet-actions draw-actions">
          <button type="button" className={primaryClass} disabled={busy} onClick={() => void push()}>{busy ? t("fixedWeek.working") : t("fixedWeek.releaseCard")}</button>
          <button type="button" className={buttonClass} disabled={busy} onClick={onCancel}>{t("fixedWeek.notNow")}</button>
        </div>
      </div>
    </div>
  );
}

function pillClass(kind: FixedWeekRow["kind"]): string {
  if (kind === "assigned" || kind === "in_work") return "fw-pill is-work";
  if (kind === "handed_in") return "fw-pill is-done";
  if (kind === "revision") return "fw-pill is-rev";
  return "fw-pill";
}

function pillText(row: FixedWeekRow, t: I18n["t"]): string {
  switch (row.kind) {
    case "offered":
      return t("fixedWeek.pillOffered");
    case "assigned":
      return t("fixedWeek.pillAssigned");
    case "in_work":
      return t("fixedWeek.pillInWork");
    case "handed_in":
      return t("fixedWeek.pillHandedIn");
    case "revision":
      return t("fixedWeek.pillRevision");
    case "share":
      return row.shareOwner ? t("fixedWeek.pillShare", { name: row.shareOwner }) : t("fixedWeek.pillShareOther");
    case "waiting_rate":
      return t("fixedWeek.pillWaitingRate");
    case "next_week":
      return t("fixedWeek.pillNextWeek");
  }
}

function revisionEnd(row: FixedWeekRow): string | null {
  const opened = row.revision?.openedAt;
  return opened ? new Date(new Date(opened).getTime() + REVISION_HOURS * 3_600_000).toISOString() : row.deadlineAt;
}

/**
 * The one instant this card counts down to, and whether it is close enough to show
 * (Shawn 2026-11-03: a card carries no deadline timer of its own any more - only the
 * panel's top strip does - except an open revision's own 48h clock, and a card not yet
 * accepted gets a countdown only in its last 24h before it goes to priority dispatch).
 */
function clockOf(row: FixedWeekRow, nowMs: number): { at: string; accept: boolean; show: boolean } | null {
  switch (row.kind) {
    case "offered":
    case "waiting_rate": {
      // A fixed-only brand releases nowhere: nothing to count down to.
      if (!row.releaseAt) return null;
      const show = new Date(row.releaseAt).getTime() - nowMs <= SOON_MS;
      return { at: row.releaseAt, accept: true, show };
    }
    case "revision": {
      const end = revisionEnd(row);
      return end ? { at: end, accept: false, show: true } : null;
    }
    default:
      return null;
  }
}

/** Revision, then work, then offers, then what is done, then another editor's share and next week; inside each the soonest clock first. */
const RANK: Partial<Record<FixedWeekRow["kind"], number>> = { revision: 0, assigned: 1, in_work: 1, offered: 2, waiting_rate: 3, handed_in: 4, share: 5, next_week: 6 };

/** A FreeFrame hand-in: the click opens the card's own upload, the file is the hand-in. Opens the tab from the click itself (a window.open after an await is popup-blocked). */
async function openUpload(card: EditorCard, setError: (e: MessageKey | null) => void, onActed: OnActed) {
  setError(null);
  const tab = window.open("about:blank", "_blank");
  try {
    const result = await post<{ ok: boolean; uploadUrl: string | null }>(`/api/editor/work/${card.id}/submit`);
    const target = result.uploadUrl ?? card.handinUrl ?? HANDIN_URL;
    if (tab) tab.location.href = target;
    else window.open(target, "_blank", "noopener,noreferrer");
  } catch {
    tab?.close();
    setError("dashboard.submitErrGeneric");
  } finally {
    onActed();
  }
}

/** Price above its estimated length, both right-aligned, level with the title. No price reads "Price coming". A static rate has no length to show. */
function PriceBlock({ row, i18n, t }: { row: FixedWeekRow; i18n: I18n; t: I18n["t"] }) {
  return (
    <div className="fw-price">
      <strong>{row.payoutCents !== null ? i18n.eur(row.payoutCents) : t("fixedWeek.priceComing")}</strong>
      {row.rateType !== "static" && row.estimatedSeconds !== null && <small>{t("fixedWeek.estimate", { seconds: row.estimatedSeconds })}</small>}
    </div>
  );
}

function Tile({ row, panel, now, sheet, setSheet, onActed }: { row: FixedWeekRow; panel: FixedWeekPanelData; now: number; sheet: Sheet | null; setSheet: (s: Sheet | null) => void; onActed: OnActed }) {
  const i18n = useI18n();
  const { t } = i18n;
  const [error, setError] = useState<MessageKey | null>(null);
  const [busy, setBusy] = useState(false);
  const card = row.card;
  const zone = panel.timezone;
  const uploadUrl = card?.uploadOpen ? card.freeframeUploadUrl : null;
  const done = (note?: string) => {
    setSheet(null);
    onActed(note);
  };
  async function accept() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await post(`/api/editor/work/${row.id}/accept`);
    } catch (err) {
      setError((err instanceof ApiError && ACCEPT_ERRORS[err.message]) || "fixedWeek.errGeneric");
    } finally {
      setBusy(false);
      onActed();
    }
  }
  const handIn = () => {
    if (!card) return;
    if (card.driveDelivery) setSheet({ kind: "handin", id: row.id });
    else void openUpload(card, setError, onActed);
  };
  const clock = clockOf(row, now);
  const left = clock ? new Date(clock.at).getTime() - now : null;
  const showClock = !!clock && clock.show && left !== null;
  const inWork = row.kind === "assigned" || row.kind === "in_work";
  const grace = inWork && row.deadlineAt ? graceMinutesLeft({ deadlineAt: row.deadlineAt, graceEndsAt: row.graceEndsAt }, now) : null;
  const handedIn = row.kind === "handed_in";
  // No timer on a card that runs on the week's deadline. One running on a date of its own says so: the strip would be wrong for it.
  const ownDate = inWork && row.deadlineAt && row.deadlineAt !== panel.weeklyDeadlineAt ? row.deadlineAt : null;
  return (
    <div className={`fw-tile${row.kind === "revision" ? " is-rev" : ""}${row.kind === "waiting_rate" ? " is-dim" : ""}${handedIn ? " is-done" : ""}`} data-fw-row={row.id}>
      <span className={pillClass(row.kind)}>
        {handedIn && <span className="fw-check" aria-hidden="true">✓ </span>}
        {pillText(row, t)}
      </span>
      <div className="fw-row-head">
        <div className="fw-row-title" title={row.title}>{card ? <a href={editorCardHref(row.id)}>{row.title}</a> : row.title}</div>
        <PriceBlock row={row} i18n={i18n} t={t} />
      </div>
      {showClock && (
        <div className="fw-due is-soon">
          <span>{clock!.accept ? t("fixedWeek.priorityIn") : t("fixedWeek.dueBy", { when: day(clock!.at, i18n, zone) })}</span>
          {left! > 0 ? <b data-fw-clock>{timeLeft(left!, t)}</b> : <b className="fw-warn">{t("fixedWeek.subOverdue")}</b>}
        </div>
      )}
      {ownDate && (
        <div className="fw-due" data-fw-own-date>
          {grace !== null ? <b className="fw-warn">{t("hub.inProductionGrace", { minutes: grace })}</b> : new Date(ownDate).getTime() > now ? <span>{t("fixedWeek.dueBy", { when: day(ownDate, i18n, zone) })}</span> : <b className="fw-warn">{t("fixedWeek.subOverdue")}</b>}
        </div>
      )}
      {row.kind === "handed_in" && row.handedInAt && <div className="fw-note">{t("fixedWeek.subHandedIn", { when: inZone(row.handedInAt, i18n, zone, { weekday: "short" }) })}</div>}
      {row.kind === "offered" && row.requestedForYou && <div className="fw-note">{t("fixedWeek.subRequested")}</div>}
      {row.kind === "waiting_rate" && <div className="fw-note">{t("fixedWeek.subWaitingRate")}</div>}
      {error && <p className="fw-error" role="alert">{t(error)}</p>}
      <div className="fw-acts draw-actions">
        {row.kind === "offered" && <button type="button" className="draw-button draw-primary" disabled={!row.canAccept || busy} onClick={() => void accept()}>{busy ? t("fixedWeek.working") : t("fixedWeek.accept")}</button>}
        {inWork && (uploadUrl ? (
          <a className="draw-button draw-primary" href={uploadUrl} target="_blank" rel="noopener noreferrer">{t("dashboard.uploadOpenLink")}</a>
        ) : (
          <button type="button" className="draw-button draw-primary" disabled={!row.canHandIn} onClick={handIn}>{t("fixedWeek.handIn")}</button>
        ))}
        {row.kind === "revision" && card && <button type="button" className="draw-button draw-primary" disabled={!row.canHandIn} onClick={handIn}>{t("fixedWeek.handInRevision")}</button>}
        {row.kind === "handed_in" && card?.driveNewRound && <button type="button" className="draw-button draw-primary" disabled={!row.canHandIn} onClick={handIn}>{t("dashboard.driveNewRound")}</button>}
        {row.push && <button type="button" className="draw-button" data-fw-release onClick={() => setSheet({ kind: "push", id: row.id })}>{t("fixedWeek.releaseCard")}</button>}
      </div>
      {sheet?.kind === "push" && sheet.id === row.id && row.push && (
        <ReleaseCardConfirm workItemId={row.id} title={row.title} terms={row.push} timezone={zone} onDone={done} onCancel={() => setSheet(null)} primaryClass="draw-button draw-primary" buttonClass="draw-button" />
      )}
      {sheet?.kind === "handin" && sheet.id === row.id && (
        <div className="fw-modal">
          <div className="fw-sheet">
            <DriveHandin workItemId={row.id} title={row.title} onDone={() => done()} onCancel={() => setSheet(null)} primaryClass="draw-button draw-primary" buttonClass="draw-button" />
          </div>
        </div>
      )}
    </div>
  );
}

/** Every card handed in, or only a card assigned for next week: the brand sorts last and sits dimmed - a new card brings it straight back (Shawn 2026-11-03). A brand with nothing visible at all is not shown (`visibleRows`). */
export function brandGroupRank(block: Pick<FixedWeekBrandBlock, "rows">): 0 | 1 | 2 {
  const week = block.rows.filter((r) => r.kind !== "next_week");
  if (week.length === 0) return 2;
  return week.every((r) => r.kind === "handed_in") ? 1 : 0;
}

/** What this editor sees of a brand: no colleague's share, no next week, unless a client assigned that card to them. */
function visibleRows(block: Pick<FixedWeekBrandBlock, "rows">): FixedWeekRow[] {
  return block.rows.filter((r) => r.kind !== "share" && (r.kind !== "next_week" || r.requestedForYou));
}

function BrandColumn({ block, panel, now, blockedOn, active, sheet, setSheet, onActed }: { block: FixedWeekBrandBlock; panel: FixedWeekPanelData; now: number; blockedOn: string | null; active: boolean; sheet: Sheet | null; setSheet: (s: Sheet | null) => void; onActed: OnActed }) {
  const i18n = useI18n();
  const { t } = i18n;
  const clockMs = (r: FixedWeekRow) => {
    const clock = clockOf(r, now);
    return clock ? new Date(clock.at).getTime() : Number.MAX_SAFE_INTEGER;
  };
  const tiles = visibleRows(block).sort((a, b) => (RANK[a.kind] ?? 9) - (RANK[b.kind] ?? 9) || clockMs(a) - clockMs(b));
  // An open revision stops new cards and other hand-ins on every brand. The block itself is the server's (`revisionBlocked`, `canAccept`, `canHandIn`).
  const alert = !block.revisionBlocked ? null : block.rows.some((r) => r.kind === "revision") ? t("fixedWeek.metaBlockedHere") : blockedOn ? t("fixedWeek.metaBlockedBy", { brand: blockedOn }) : t("fixedWeek.metaBlocked");
  const dim = brandGroupRank(block) > 0;
  return (
    <div className={`fw-col${dim ? " fw-col-dim" : ""}`} data-fw-brand={block.brandId} data-fw-active={active}>
      <div className="fw-col-head">
        <span className="fw-avatar" aria-hidden="true">{initials(block.name)}</span>
        <div className="fw-brand-copy">
          <div className="fw-brand-name">{block.name}</div>
          {block.weeklyQuota !== null && <div className="fw-brand-meta">{t("fixedWeek.weeklyLimit", { done: block.acceptedThisWeek, limit: block.weeklyQuota })}</div>}
        </div>
      </div>
      {block.pips.length > 0 && (
        <div className="fw-pips" role="img" aria-label={t("fixedWeek.pipsLabel")}>
          {block.pips.map((p, i) => <span key={i} className={`fw-pip is-${p}`} />)}
        </div>
      )}
      {alert && <div className="fw-team is-warn" data-fw-alert>{alert}</div>}
      <div className="fw-tiles">
        {tiles.map((row) => <Tile key={row.id} row={row} panel={panel} now={now} sheet={sheet} setSheet={setSheet} onActed={onActed} />)}
      </div>
    </div>
  );
}

function titleOf(panel: FixedWeekPanelData, t: I18n["t"]): string {
  const week = panel.weekNumber;
  if (panel.revisions === 1) return t("fixedWeek.titleRevisionOne", { week });
  if (panel.revisions > 1) return t("fixedWeek.titleRevisionMany", { week, count: panel.revisions });
  const free = panel.releaseSoon?.count ?? 0;
  if (free === 1) return t("fixedWeek.titleFreeOne", { week });
  if (free > 1) return t("fixedWeek.titleFreeMany", { week, count: free });
  return t("fixedWeek.title", { week });
}

/** The soonest open revision: the only thing that gets a strip of its own. */
function firstRevision(panel: FixedWeekPanelData): { row: FixedWeekRow; brand: string; at: string | null } | null {
  const all = panel.brands.flatMap((b) => b.rows.filter((r) => r.kind === "revision").map((row) => ({ row, brand: b.name, at: revisionEnd(row) })));
  return all.sort((a, b) => (a.at ?? "9").localeCompare(b.at ?? "9"))[0] ?? null;
}

export function FixedWeekPanel({ panel, onActed }: { panel: FixedWeekPanelData; onActed: OnActed }) {
  const i18n = useI18n();
  const { t } = i18n;
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const now = useNow();
  const blockedOn = panel.brands.find((b) => b.rows.some((r) => r.kind === "revision"))?.name ?? null;
  const soon = panel.releaseSoon;
  const hoursToRelease = soon ? Math.max(1, Math.ceil((new Date(soon.at).getTime() - now) / 3_600_000)) : 0;
  const strip = firstRevision(panel);
  // A brand with nothing for this editor to see this week is left out entirely (Shawn 2026-11-04). The rest: active brands
  // first, then every-card-handed-in, then only a card assigned for next week - both of the last two dimmed (Shawn 2026-11-03).
  const ordered = useMemo(() => panel.brands.filter((b) => visibleRows(b).length > 0).sort((a, b) => brandGroupRank(a) - brandGroupRank(b)), [panel.brands]);
  const activeBrandId = selectedBrand && ordered.some((b) => b.brandId === selectedBrand) ? selectedBrand : (ordered[0]?.brandId ?? null);
  const weekDeadlineMs = new Date(panel.weeklyDeadlineAt).getTime();
  return (
    <section className="stat-card fw-panel" data-section="fixed-week" aria-label={t("fixedWeek.eyebrow")}>
      <div className="stat-card-header">
        <span className="stat-label">{t("fixedWeek.eyebrow")} · {titleOf(panel, t)}</span>
        <div className="fw-chips">
          {panel.revisions > 0 && <span className="fw-chip is-warn">{t("fixedWeek.chipRevision", { count: panel.revisions })}</span>}
          <span className="fw-chip">{t("fixedWeek.chipInWork", { count: panel.inWork })}</span>
          <span className="fw-chip">{t("fixedWeek.chipOffered", { count: panel.offered })}</span>
        </div>
      </div>
      <div className="fw-week-strip" data-fw-week-strip>
        <div>
          <div className="fw-brand-meta">{t("fixedWeek.weekStripLabel")}</div>
          <div className="fw-strip-title">{day(panel.weeklyDeadlineAt, i18n, panel.timezone)}</div>
        </div>
        {weekDeadlineMs > now ? <b className="fw-strip-left" data-fw-clock>{timeLeft(weekDeadlineMs - now, t)}</b> : <b className="fw-warn">{t("fixedWeek.subOverdue")}</b>}
      </div>
      {soon && (
        <div className="fw-banner" data-fw-banner>
          {soon.count === 1 ? t("fixedWeek.bannerReleaseOne", { hours: hoursToRelease }) : t("fixedWeek.bannerReleaseMany", { count: soon.count, hours: hoursToRelease })}
        </div>
      )}
      {strip && (
        <a className="fw-strip" data-fw-strip href={editorCardHref(strip.row.id)}>
          <div>
            <div className="fw-brand-meta">{t("fixedWeek.stripTitle")}</div>
            <div className="fw-strip-title">{strip.row.title}</div>
            <div className="fw-brand-meta">{strip.brand}</div>
          </div>
          {strip.at && (
            <div className="fw-strip-clock">
              {new Date(strip.at).getTime() > now ? <b data-fw-clock>{timeLeft(new Date(strip.at).getTime() - now, t)}</b> : <b className="fw-warn">{t("fixedWeek.subOverdue")}</b>}
              <span>{day(strip.at, i18n, panel.timezone)}</span>
            </div>
          )}
        </a>
      )}
      {ordered.length > 1 && (
        <div className="fw-tabs" role="tablist">
          {ordered.map((block) => (
            <button
              key={block.brandId}
              type="button"
              role="tab"
              aria-selected={block.brandId === activeBrandId}
              className={`fw-tab${block.brandId === activeBrandId ? " is-active" : ""}${brandGroupRank(block) > 0 ? " fw-col-dim" : ""}`}
              onClick={() => setSelectedBrand(block.brandId)}
            >
              {block.name} <span className="fw-tab-count">{visibleRows(block).length}</span>
            </button>
          ))}
        </div>
      )}
      {ordered.length > 0 ? (
        <div className="fw-columns">
          {ordered.map((block) => (
            <BrandColumn key={block.brandId} block={block} panel={panel} now={now} blockedOn={blockedOn} active={block.brandId === activeBrandId} sheet={sheet} setSheet={setSheet} onActed={onActed} />
          ))}
        </div>
      ) : (
        <div className="fw-col-empty" data-fw-empty>{t("fixedWeek.allEmpty")}</div>
      )}
    </section>
  );
}
